import { z } from 'zod';
/**
 * Public account contract for `/api/v1` (KIR-4431, slice 4).
 *
 * Field names follow `apps/backend/src/routes/v1/NAMING.md`.
 *
 * **`accounts`, not `users`.** A row here is a person who holds a Kirin account
 * in your organization — someone who can sign in. That is a different population
 * from `inventory-users`, which is the developers the agent observes and may
 * include people with no account at all. Naming both `users` would hide the
 * difference at exactly the moment a caller has to care about it; see the
 * vocabulary table in `NAMING.md`.
 *
 * That distinction is enforced, not just asserted: the internal handler behind
 * these endpoints also returns the observed-developer rows, because the app
 * renders both populations on one screen. They are filtered out before anything
 * here is published — see `accountsOnly` in the backend adapter.
 *
 * **Writes are the most consequential on this surface**, and three things about
 * this resource are worth reading before the schemas.
 *
 * ## The invite link is not published
 *
 * Creating an account returns, internally, the raw single-use token behind the
 * `?invite_token=` link. It is not published here. Only its hash is stored, the
 * internal code returns it in exactly one place and never on a list, and — the
 * deciding point — the invitee is emailed the link regardless. So publishing it
 * would add a permanent distribution channel for a bearer credential that grants
 * organization membership, inside a contract frozen for the life of `v1`, for no
 * capability a caller does not already have.
 *
 * ## Roles are verified live on every write
 *
 * A key carries the role snapshot from when it was minted. On these endpoints
 * that snapshot is intersected with the *current* roles of whoever minted the
 * key, so a key whose minter has since been demoted or removed cannot change
 * anyone's role or remove anyone. See `v1RoleGateHook`.
 *
 * ## Reading the list needs manager, owner or support
 *
 * Not any role, unlike most reads on this API. The app shows the membership list
 * to those three only, and a key must not be a way around the gate its holder is
 * subject to.
 */
/** What a person can do in the organization. */
export const v1AccountRoleSchema = z
    .enum(['owner', 'manager', 'member', 'support'])
    .describe('The role this person holds. `owner` has full control; `manager` administers without being able to transfer ownership; `member` is ordinary access; `support` reads everything and writes nothing.');
/** Where a person is in the joining process. */
export const v1AccountStatusSchema = z
    .enum(['active', 'invited', 'disabled'])
    .describe('Whether this person has accepted their invitation and can sign in (`active`), has been invited but not yet accepted (`invited`), or has been disabled (`disabled`). Only `active` people count toward the rule that an organization must keep at least one owner.');
/**
 * One person holding an account in your organization.
 *
 * **`roles` is a list, not a single role.** The underlying identity provider
 * models it that way and a person can genuinely hold more than one. Publishing a
 * scalar would mean picking one and hiding the rest.
 *
 * **Linked source-control identities are not published.** The internal row can
 * carry linked GitHub and Bitbucket accounts. They belong to the person rather
 * than to their Kirin account, are populated only where the relevant integration
 * is connected, and are not needed to administer membership — which is what this
 * resource is for.
 */
export const v1AccountSchema = z
    .object({
    id: z
        .string()
        .describe("This person's identifier — the `accountId` path parameter of the role and removal endpoints. A different identifier space from an inventory user's: the two resources never share an ID, so one cannot be addressed through the other."),
    name: z.string().nullable().describe('Display name, if known.'),
    email: z
        .string()
        .nullable()
        .describe('Email address, if known. Null is possible for a record the identity provider has not filled in.'),
    roles: z
        .array(v1AccountRoleSchema)
        .describe('Every role this person holds. Usually one, but the identity provider permits more.'),
    status: v1AccountStatusSchema
        .nullable()
        .describe('Where this person is in the joining process, or null when the identity provider did not report one.'),
    hasPendingInvite: z
        .boolean()
        .describe('Whether an invitation is outstanding for this person. Distinct from `status`: someone added through device management can read as `invited` with no invitation to resend, so this is the field to branch on when deciding whether an invite is actually in flight.'),
    createdAt: z
        .string()
        .nullable()
        .describe('When this account was created, ISO 8601 UTC, or null when the identity provider did not report it.'),
})
    .meta({
    id: 'V1Account',
    title: 'Account',
    description: 'One person holding a Kirin account in your organization. Not the same population as inventory users — those are the endpoint users the agent observes, who may have no account.',
    examples: [
        {
            id: 'U2p8XmKvQ7RtLbN4sYwZ9hJ3dFcT',
            name: 'Roger Wilco',
            email: 'roger@example.com',
            roles: ['manager'],
            status: 'active',
            hasPendingInvite: false,
            createdAt: '2026-04-14T07:55:02Z',
        },
    ],
});
/** The account list. Not paginated — an organization's membership is small. */
export const v1AccountListResponseSchema = z
    .object({
    data: z
        .array(v1AccountSchema)
        .describe('Everyone holding an account in your organization.'),
})
    .meta({
    id: 'V1AccountList',
    title: 'Account list',
    description: "Everyone holding a Kirin account in your organization, including people invited but not yet active. Not paginated: an organization's membership is small and the identity provider returns it whole.",
    examples: [
        {
            data: [
                {
                    id: 'U2p8XmKvQ7RtLbN4sYwZ9hJ3dFcT',
                    name: 'Roger Wilco',
                    email: 'roger@example.com',
                    roles: ['manager'],
                    status: 'active',
                    hasPendingInvite: false,
                    createdAt: '2026-04-14T07:55:02Z',
                },
                {
                    id: 'U3kR7vNbXwQ2mYtZ8sLcH4jFdA9p',
                    name: null,
                    email: 'newjoiner@example.com',
                    roles: ['member'],
                    status: 'invited',
                    hasPendingInvite: true,
                    createdAt: '2026-08-30T16:18:44Z',
                },
            ],
        },
    ],
});
/**
 * Invite someone to the organization.
 *
 * The person is emailed an invitation. If they have no Kirin account yet, one is
 * created in the `invited` state; they become `active` when they accept.
 *
 * **Safe to retry.** Inviting an address that already holds an account, or whose
 * invitation is still outstanding, answers 200 with that account rather than a
 * conflict — the state asked for already holds. Nothing on it is changed: no
 * second email, no rotated invitation link (which would break one the invitee may
 * be about to click), and no role applied. Use the role endpoint to change a
 * role, and remove-then-invite to issue a fresh invitation.
 */
export const v1AccountInviteSchema = z
    .object({
    email: z
        .string()
        .email()
        .describe('The email address to invite. The invitation is sent there.'),
    role: v1AccountRoleSchema
        .optional()
        .describe('The role to grant on acceptance. Defaults to `member`. Granting `owner` requires that your key was minted by an owner; granting `support` requires a @knostic.ai address. Ignored when the address already holds an account or an outstanding invitation — see the note above.'),
})
    .meta({
    id: 'V1AccountInvite',
    title: 'Invite an account',
    description: 'The email address to invite and the role it should receive on acceptance.',
    examples: [{ email: 'newjoiner@example.com', role: 'member' }],
});
/** Change someone's role. */
export const v1AccountRoleUpdateSchema = z
    .object({
    role: v1AccountRoleSchema.describe('The role to set. Answers 403 if the change would leave the organization with no active owner, or if your key was not minted by an owner and the change involves the owner role — both are refusals of the effect, not of the request. Setting `support` requires a @knostic.ai address and answers 400 without one.'),
})
    .meta({
    id: 'V1AccountRoleUpdate',
    title: 'Update an account role',
    description: 'The role to set on a person.',
    examples: [{ role: 'manager' }],
});
//# sourceMappingURL=accounts.js.map